import { ExpressionSpecification, FilterSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import { COLOR_TEXT_PRIMARY, COLOR_TEXT_PRIMARY_INVERSE, COLOR_TEXT_SECONDARY, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { useExperiments } from '@alltrails/experiments';
import useMap from '../../hooks/useMap';
import { buildDynamicTrailPinProps, buildNewRegularTrailIconImage, buildRegularTrailIconImage } from '../../utils/buildPinIconExpressions';
import { getLayerBeforeId, regularTrailPinsId } from '../../utils/layers';
import { newPinMarkerVerticalOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import { isTrailClosedExpression, isTrailCollapsedExpression } from '../../utils/trailPinConstants';

type RegularTrailPinLayerProps = {
  sourceId: string;
  isHoveredExpression: ExpressionSpecification;
  isSelectedExpression: ExpressionSpecification;
  isViewedExpression: ExpressionSpecification;
  isActiveTrailExpression: ExpressionSpecification;
  commonLayoutProps: SymbolLayerSpecification['layout'];
};

const RegularTrailPinLayer = ({
  sourceId,
  isHoveredExpression,
  isSelectedExpression,
  isViewedExpression,
  isActiveTrailExpression,
  commonLayoutProps
}: RegularTrailPinLayerProps) => {
  const map = useMap();
  const experiments = useExperiments();
  const hasNewPinsExperiment = experiments?.['web-discover-new-pin-explore-rm']?.value === 'on';
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];
  const regularTrailIconImage = buildRegularTrailIconImage(isHoveredExpression, isSelectedExpression, isActiveTrailExpression);
  const newRegularTrailIconImage = buildNewRegularTrailIconImage(
    isHoveredExpression,
    isSelectedExpression,
    isViewedExpression,
    isActiveTrailExpression
  );
  const newPinLayoutProps = buildDynamicTrailPinProps(isHoveredExpression, isSelectedExpression);

  return (
    <>
      <Layer
        source={sourceId}
        type="symbol"
        id={regularTrailPinsId}
        beforeId={getLayerBeforeId(map, regularTrailPinsId)}
        filter={['all', ['!', ['has', 'point_count']], ['!', isTrailCollapsedExpression]] as FilterSpecification}
        layout={{
          ...(hasNewPinsExperiment ? {} : commonLayoutProps),
          'icon-image': hasNewPinsExperiment ? newRegularTrailIconImage : regularTrailIconImage,
          ...(hasNewPinsExperiment
            ? {
                ...newPinLayoutProps,
                'text-offset': [0, (-1 * newPinMarkerVerticalOffset) / FONT_SIZE_100]
              }
            : {})
        }}
        paint={{
          ...(hasNewPinsExperiment
            ? {
                'text-color': [
                  'case',
                  isActiveOrHovered,
                  COLOR_TEXT_PRIMARY_INVERSE, // White text on active/hover (green background)
                  // Note: saved/completed/closed pins now have white background in default state,
                  // so they need dark text, not white
                  isTrailClosedExpression,
                  COLOR_TEXT_SECONDARY, // Dark text on closed (light gray background)
                  COLOR_TEXT_PRIMARY // Default dark text (for trail, saved, completed in default state)
                ]
              }
            : {})
        }}
      />
    </>
  );
};

export default RegularTrailPinLayer;
